
import React, { useState } from 'react';
import { TreatmentPlan } from '../types';
import { CORES_CROMOTERAPIA } from '../constants';
import { ArrowLeft, Printer, Share2, Check, Zap, MessageCircle, Copy, Focus, Brain, Heart, MapPin, Timer, ShieldCheck, Signature } from 'lucide-react';

interface Props {
  plan: TreatmentPlan;
  onBack: () => void;
}

const PlanDisplay: React.FC<Props> = ({ plan, onBack }) => {
  const [copied, setCopied] = useState(false);

  const getPublicLink = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?p=${plan.id}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getPublicLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    const link = getPublicLink();
    const text = `Olá! Segue o seu Protocolo de Cromoterapia Aplicada (Metodologia João Caputo).\n\nAssistido: ${plan.patientName}\nData: ${plan.dateCreated}\n\nVocê pode visualizar os detalhes completos clicando no link abaixo:\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handlePrint = () => window.print();

  return (
    <div className="space-y-10 pb-20">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6 no-print">
        <button onClick={onBack} className="flex items-center gap-3 text-chroma-muted hover:text-chroma-accent transition-colors self-start font-bold text-lg">
          <ArrowLeft size={24} />
          Voltar
        </button>
        <div className="flex flex-wrap gap-4 w-full sm:w-auto">
          <button 
            onClick={handleWhatsAppShare}
            className="flex-1 sm:flex-none flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-4 rounded-2xl text-sm font-bold shadow-lg transition-all transform hover:scale-105"
          >
            <MessageCircle size={20} />
            Enviar no WhatsApp
          </button>
          <button 
            onClick={handleCopyLink}
            className="flex-1 sm:flex-none flex items-center justify-center gap-3 bg-white dark:bg-white/5 text-chroma-text px-6 py-4 rounded-2xl text-sm font-bold border border-purple-200 shadow-sm"
          >
            {copied ? <Check size={20} className="text-emerald-500" /> : <Share2 size={20} className="text-chroma-accent" />}
            Link Público
          </button>
          <button onClick={handlePrint} className="flex-1 sm:flex-none flex items-center justify-center gap-3 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 px-6 py-4 rounded-2xl text-sm font-bold border border-purple-300 shadow-sm">
            <Printer size={20} />
            Imprimir
          </button>
        </div>
      </div>

      <div className="bg-chroma-card text-chroma-text rounded-[2.5rem] shadow-2xl overflow-hidden border border-chroma-border print-card">
        {/* Cabeçalho do Protocolo */}
        <div className={`p-12 text-white print:text-black ${plan.generationMethod === 'AI' ? 'bg-gradient-to-r from-indigo-800 to-purple-900' : 'bg-slate-800'}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-4 opacity-70">
                 <ShieldCheck size={14} />
                 <p className="text-[10px] font-black uppercase tracking-[0.4em]">Protocolo Técnico Metodológico</p>
              </div>
              <h2 className="text-5xl font-bold tracking-normal">{plan.patientName}</h2>
              <div className="flex items-center gap-6 mt-6">
                <div className="flex flex-col">
                   <span className="text-[9px] uppercase font-bold opacity-50">Data de Geração</span>
                   <span className="text-base font-light tracking-wide">{plan.dateCreated}</span>
                </div>
                <div className="flex flex-col border-l border-white/20 pl-6">
                   <span className="text-[9px] uppercase font-bold opacity-50">Método de Análise</span>
                   <span className="text-base font-light tracking-wide">{plan.generationMethod === 'AI' ? 'Processamento IA' : 'Mapeamento Local'}</span>
                </div>
              </div>
            </div>
            <div className="p-8 bg-white/10 rounded-[2rem] backdrop-blur-md hidden sm:block">
              <Brain className="text-chroma-accent" size={56} />
            </div>
          </div>
        </div>

        <div className="p-12 space-y-14">
          <div className="bg-purple-50 dark:bg-white/5 p-10 rounded-[2.5rem] border border-purple-100 dark:border-white/10 shadow-inner relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5">
               <Zap size={100} className="text-purple-500" />
            </div>
            <h3 className="text-[10px] font-black uppercase text-purple-600 dark:text-purple-400 mb-6 tracking-[0.3em] flex items-center gap-2">
              <Focus size={14} /> Parecer Terapêutico
            </h3>
            <p className="text-xl italic leading-loose text-chroma-text font-light tracking-wide relative z-10">{plan.resumo}</p>
          </div>

          <div className="space-y-24">
            {plan.passos.map((passo, idx) => {
              const corInfo = CORES_CROMOTERAPIA.find(c => c.cor === passo.cor);
              return (
                <div key={idx} className="relative pl-14 border-l-2 border-purple-200 dark:border-purple-800/50">
                  <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-chroma-accent flex items-center justify-center text-sm text-white font-black shadow-lg">
                    {idx + 1}
                  </div>
                  
                  <div className="space-y-8">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                       <span className="w-fit text-[10px] font-black uppercase text-chroma-accent bg-chroma-accent/10 px-5 py-2 rounded-full tracking-[0.2em] border border-chroma-accent/20">{passo.fase}</span>
                       <h4 className="font-bold text-3xl tracking-normal">{passo.descricao}</h4>
                    </div>

                    <div className="flex flex-wrap gap-4">
                       <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/30 px-6 py-3 rounded-2xl text-sm font-bold text-blue-800 dark:text-blue-200 border border-blue-200/50">
                          <MapPin size={18} /> {passo.regiao}
                       </div>
                       {passo.cor && (
                          <div className="flex items-center gap-3 bg-chroma-cardSecondary px-6 py-3 rounded-2xl text-sm font-bold border border-chroma-border shadow-sm">
                             <div className="w-4 h-4 rounded-full shadow-lg" style={{ backgroundColor: corInfo?.hex }}></div>
                             {passo.cor}
                          </div>
                       )}
                       {passo.tempo && (
                          <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/30 px-6 py-3 rounded-2xl text-sm font-bold text-emerald-800 dark:text-emerald-200 border border-emerald-200/50">
                             <Timer size={18} /> {passo.tempo}
                          </div>
                       )}
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                       <div className="bg-purple-50/50 dark:bg-white/5 p-10 rounded-[2.5rem] border border-purple-100 dark:border-white/10 text-xl font-light leading-loose tracking-wide shadow-sm text-chroma-text">
                          {passo.detalhes}
                       </div>
                       {passo.ideoplastia && (
                          <div className="bg-chroma-accent/5 border border-chroma-accent/20 p-10 rounded-[2.5rem] flex flex-col gap-6 shadow-sm">
                             <div className="flex items-center gap-4">
                                <Focus className="text-chroma-accent shrink-0" size={32} />
                                <h5 className="text-[10px] font-black uppercase text-chroma-accent tracking-[0.3em]">Mentalização (Ideoplastia)</h5>
                             </div>
                             <p className="text-xl italic font-light leading-loose tracking-wide text-chroma-text opacity-90">
                                {passo.ideoplastia}
                             </p>
                          </div>
                       )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-20 border-t border-chroma-border grid grid-cols-1 md:grid-cols-2 gap-12">
             <div className="bg-amber-500/5 border border-amber-500/20 p-10 rounded-[2.5rem] flex flex-col gap-6">
                <Heart className="text-amber-500 shrink-0" size={32} />
                <p className="text-lg text-chroma-text italic font-light leading-relaxed tracking-wide">
                   "A luz deve percorrer e cobrir o sistema ou órgão a ser tratado. A sua concentração no direcionamento da energia é fundamental para o resultado."
                </p>
                <p className="text-[9px] font-black uppercase tracking-widest text-amber-600">— João Caputo</p>
             </div>

             <div className="flex flex-col justify-end items-center md:items-end space-y-4">
                <div className="w-full max-w-xs border-b border-purple-300 dark:border-white/20 h-24 flex items-end justify-center">
                   <Signature className="text-purple-200 dark:text-white/10 opacity-50" size={60} />
                </div>
                <div className="text-center md:text-right">
                   <p className="font-bold text-sm tracking-widest uppercase">Assinatura do Terapeuta</p>
                   <p className="text-[10px] text-chroma-muted uppercase mt-1">Especialista em Cromoterapia Aplicada</p>
                </div>
             </div>
          </div>
          
          <div className="bg-gray-100 dark:bg-white/5 p-6 rounded-2xl text-[9px] text-center text-chroma-muted font-bold tracking-widest uppercase opacity-40">
            Documento gerado pelo Portal Oficial João Caputo V2.0 - Cromoterapia Aplicada &copy; {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDisplay;
